import { Component, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { AlertService } from "./common/services/alert.service";
import { CrmService } from "./common/services/crm.service";
import { SettingsService } from "./common/services/settings.service";
import { LoginService } from "./login/login.service";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
})
export class AppComponent {
  constructor(
    private platform: Platform,
    private router: Router,
    private zone: NgZone,
    private translate: TranslateService,
    private settingsService: SettingsService,
    private crmService: CrmService,
    private loginService: LoginService,
    private alertService: AlertService
  ) {
    this.setDefaultLanguage();
    this.initializeApp();
    this.resumeApp();

    this.platform.ready().then(async () => {
      this.solloLog();

      if (Capacitor.isNativePlatform()) {
        await SplashScreen.show({ autoHide: true, showDuration: 2000 });
      }

      await this.setupEdgeToEdge();
    });
  }

  /** Enable edge-to-edge and set proper system bar colors/styles */
  private async setupEdgeToEdge() {
    if (!Capacitor.isNativePlatform() || !this.platform.is('android')) return;

    try { await EdgeToEdge.enable(); } catch {}

    await EdgeToEdge.setBackgroundColor({ color: '#1a1a1a' });
    await StatusBar.setStyle({ style: Style.Dark });

  }


  private async applyDarkSystemBars() {
    if (!Capacitor.isNativePlatform() || !this.platform.is('android')) return;
    await EdgeToEdge.setBackgroundColor({ color: '#1a1a1a' });
    await StatusBar.setStyle({ style: Style.Dark });
  }


  private setDefaultLanguage(): void {
    const lang = this.translate.getBrowserCultureLang() || "en-CA";
    this.translate.setDefaultLang(lang);
  }

  private initializeApp() {
    this.loginService.user$.subscribe((user) => {
      if (user && (this.router.url === "/" || this.router.url === "/login")) {
        const isFirstAccess = localStorage.getItem("isFirstAccess");
        if (isFirstAccess && JSON.parse(isFirstAccess) === false) {
          this.router.navigate(["/tabs/tab1"]).then();
        } else {
          this.router.navigate(["/welcome"]).then();
        }
      }
    });

    App.addListener("appUrlOpen", (event: URLOpenListenerEvent) => {
      this.loginService.user$.subscribe({
        next: (userLogin) => {
          this.zone.run(() => {
            const domain = "sollo.cards";
            const pathArray = event.url.split(domain);
            const appPath = pathArray.pop();
            if (appPath) {
              if (appPath.includes("/a/")) {
                if (userLogin) {
                  this.alertService
                    .showWindowAlert(
                      this.translate.instant("COMMON.ALERTS.signoutTitle"),
                      this.translate.instant("COMMON.ALERTS.logoutToActivateMessage")
                    )
                    .then((data) => {
                      if (data) {
                        this.loginService.logout().then(() =>
                          this.router.navigateByUrl("login" + appPath).then()
                        );
                      }
                    });
                }
                this.router.navigateByUrl("login" + appPath).then();
              } else {
                if (userLogin) {
                  this.router.navigateByUrl("tabs/tab2").then(() =>
                    this.router.navigateByUrl("tabs/tab2/contact/sollo" + appPath, {
                      state: { saveContact: true },
                    })
                  );
                } else {
                  this.showContactNoLogin(appPath).then();
                }
              }
            }
          });
        },
      });
    }).then();
  }

  private resumeApp() {
    document.addEventListener('resume', () => {
      this.settingsService.getUserSettings().then();
      this.crmService.getCurrentCRM().then();
      this.applyDarkSystemBars().then();
    });

  }

  private async showContactNoLogin(path: string) {
    await Browser.open({ url: `https://sollo.cards/${path}` });
  }

  private solloLog() {
    console.info(
      "\n" +
      "   _____       ____    \n" +
      "  / ___/____  / / /___ \n" +
      "  \\__ \\/ __ \\/ / / __ \\\n" +
      " ___/ / /_/ / / / /_/ /\n" +
      "/____/\\____/_/_/\\____/ \n" +
      "                       \n" +
      "Business Intelligence \n" +
      "--------------------------- \n" +
      "www.sollo.digital \n "
    );
  }
}
